/**
 * Tracks the Fuego app banner injected at the top of the viewport and
 * exposes its current height as the `--fuego-banner-height` CSS variable
 * on :root. CSS (e.g. the mobile menu drawer) can then account for it
 * so that fixed-position elements stay fully within the visible area.
 */
(function () {
  const BANNER_SELECTOR =
    '#fuego-app-banner, .fuego-app-banner, [id^="fuego-app-banner"], [class*="fuego-app-banner"]'
  const CSS_VAR = '--fuego-banner-height'

  let currentBanner = null
  let resizeObserver = null
  let rafId = null

  const setHeight = (pxValue) => {
    const value = `${Math.max(0, Math.round(pxValue))}px`
    if (document.documentElement.style.getPropertyValue(CSS_VAR) !== value) {
      document.documentElement.style.setProperty(CSS_VAR, value)
    }
  }

  const measure = (el) => {
    if (!el || !el.isConnected) {
      setHeight(0)
      return
    }
    const rect = el.getBoundingClientRect()
    const style = window.getComputedStyle(el)
    const hidden =
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      rect.height === 0
    setHeight(hidden ? 0 : rect.height)
  }

  const scheduleMeasure = (el) => {
    if (rafId) cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(() => measure(el))
  }

  const findBanner = () => document.querySelector(BANNER_SELECTOR)

  const observeForInjection = () => {
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  const attach = (el) => {
    if (currentBanner === el) return
    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
    mutationObserver.disconnect()
    currentBanner = el
    if (!el) {
      setHeight(0)
      observeForInjection()
      return
    }
    mutationObserver.observe(el, {
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    })
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => scheduleMeasure(el))
      resizeObserver.observe(el)
    }
    scheduleMeasure(el)
  }

  const findBannerInRecords = (records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!node || node.nodeType !== 1) continue
        if (node.matches && node.matches(BANNER_SELECTOR)) return node
        if (node.querySelector) {
          const nested = node.querySelector(BANNER_SELECTOR)
          if (nested) return nested
        }
      }
    }
    return null
  }

  const sync = (records) => {
    if (!currentBanner) {
      const found = records && records.length
        ? findBannerInRecords(records)
        : findBanner()
      if (found) attach(found)
      return
    }
    if (!currentBanner.isConnected) {
      attach(null)
      return
    }
    scheduleMeasure(currentBanner)
  }

  const mutationObserver = new MutationObserver(sync)

  const init = () => {
    const initial = findBanner()
    if (initial) attach(initial)
    else observeForInjection()
    window.addEventListener('resize', () => scheduleMeasure(currentBanner), {
      passive: true,
    })
    window.addEventListener('orientationchange', () =>
      scheduleMeasure(currentBanner)
    )
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true })
  } else {
    init()
  }
})()
